import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, RefreshCw, X, Send, Eye, CheckCircle2, MailX } from 'lucide-react';
import { campaignsAPI } from '../../lib/api';
// P_USER_WELCOME_DISPATCH_2.1 — proper UTC→local conversion.
import { fmtLocalShort } from '../../utils/localTime';

/**
 * P_USER_WELCOME_DISPATCH_3.0a — Campaigns admin table.
 * Read-only; campaign authoring happens in the Builder modal.
 */

const STATUS_TONE = {
    draft:      'bg-muted text-muted-foreground border-border',
    scheduled:  'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-900',
    sending:    'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900',
    completed:  'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900',
    cancelled:  'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-900',
};

export const CampaignsTable = ({ refreshKey, onOpenDrawer, onEditDraft, statusFilter: forcedStatusFilter = null }) => {
    const { t, i18n } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState(forcedStatusFilter || '');
    const [typeFilter, setTypeFilter] = useState('');
    const [cancelingId, setCancelingId] = useState(null);

    useEffect(() => {
        if (forcedStatusFilter !== null) setStatusFilter(forcedStatusFilter);
    }, [forcedStatusFilter]);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (search) params.search = search;
            if (statusFilter) params.status = statusFilter;
            if (typeFilter) params.type = typeFilter;
            const r = await campaignsAPI.list(params);
            setRows(r.data?.campaigns || []);
        } catch (_e) { setRows([]); }
        finally { setLoading(false); }
    }, [search, statusFilter, typeFilter]);

    useEffect(() => { reload(); }, [reload, refreshKey]);

    const handleCancel = async (id) => {
        setCancelingId(id);
        try {
            await campaignsAPI.cancel(id);
            await reload();
        } catch (_e) {
            /* surfaces via reload showing unchanged row */
        } finally {
            setCancelingId(null);
        }
    };

    return (
        <Card className="p-4 md:p-5 space-y-4" data-testid="campaigns-panel">
            {/* Filter row */}
            <div className="flex flex-col md:flex-row gap-2" data-testid="campaigns-filters">
                <input value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('campaignsTable.filters.search')}
                    className="bg-background border border-input rounded-md h-9 px-3 text-sm text-foreground md:flex-1"
                    data-testid="campaigns-filter-search" />
                {forcedStatusFilter === null ? (
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="bg-background border border-input rounded-md h-9 px-3 text-sm text-foreground md:w-44"
                    data-testid="campaigns-filter-status">
                    <option value="">{t('campaignsTable.filters.allStatuses')}</option>
                    <option value="draft">{t('campaignsTable.status.draft')}</option>
                    <option value="scheduled">{t('campaignsTable.status.scheduled')}</option>
                    <option value="sending">{t('campaignsTable.status.sending')}</option>
                    <option value="completed">{t('campaignsTable.status.completed')}</option>
                    <option value="cancelled">{t('campaignsTable.status.cancelled')}</option>
                </select>
                ) : null}
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                    className="bg-background border border-input rounded-md h-9 px-3 text-sm text-foreground md:w-44"
                    data-testid="campaigns-filter-type">
                    <option value="">{t('campaignsTable.filters.allTypes')}</option>
                    <option value="announcement">{t('campaignBuilder.type.announcement')}</option>
                    <option value="training">{t('campaignBuilder.type.training')}</option>
                    <option value="recognition">{t('campaignBuilder.type.recognition')}</option>
                    <option value="birthday">{t('campaignBuilder.type.birthday')}</option>
                    <option value="emergency">{t('campaignBuilder.type.emergency')}</option>
                    <option value="custom">{t('campaignBuilder.type.custom')}</option>
                </select>
                <Button variant="outline" size="sm" onClick={reload} disabled={loading} data-testid="campaigns-reload">
                    <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                    {t('campaignsTable.reload')}
                </Button>
            </div>

            {/* Table */}
            {loading ? (
                <div className="p-8 text-center text-muted-foreground" data-testid="campaigns-loading">
                    <Loader2 className="w-5 h-5 mx-auto animate-spin" />
                </div>
            ) : rows.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground" data-testid="campaigns-empty">
                    {t('campaignsTable.empty')}
                </div>
            ) : (
                <div className="overflow-x-auto" data-testid="campaigns-rows">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/40">
                            <tr className="text-xs uppercase text-muted-foreground">
                                <th className="text-left px-3 py-2 font-medium">{t('campaignsTable.cols.title')}</th>
                                <th className="text-left px-3 py-2 font-medium">{t('campaignsTable.cols.type')}</th>
                                <th className="text-right px-3 py-2 font-medium">{t('campaignsTable.cols.recipients')}</th>
                                <th className="text-right px-3 py-2 font-medium">
                                    <Send className="w-3.5 h-3.5 inline mr-1" />
                                    {t('campaignsTable.cols.sent')}
                                </th>
                                <th className="text-right px-3 py-2 font-medium">
                                    <Eye className="w-3.5 h-3.5 inline mr-1" />
                                    {t('campaignsTable.cols.seen')}
                                </th>
                                <th className="text-right px-3 py-2 font-medium">
                                    <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />
                                    {t('campaignsTable.cols.ack')}
                                </th>
                                <th className="text-right px-3 py-2 font-medium">
                                    <MailX className="w-3.5 h-3.5 inline mr-1" />
                                    {t('campaignsTable.cols.unopened')}
                                </th>
                                <th className="text-left px-3 py-2 font-medium">{t('campaignsTable.cols.status')}</th>
                                <th className="text-left px-3 py-2 font-medium">{t('campaignsTable.cols.created')}</th>
                                <th className="px-3 py-2 font-medium"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => {
                                const m = r.metrics || {};
                                const canCancel = r.status === 'draft' || r.status === 'scheduled';
                                const handleRowClick = () => {
                                    if (r.status === 'draft' && onEditDraft) { onEditDraft(r); return; }
                                    if (onOpenDrawer) onOpenDrawer(r.id);
                                };
                                return (
                                    <tr key={r.id}
                                        onClick={handleRowClick}
                                        className="border-b border-border last:border-b-0 hover:bg-muted/40 cursor-pointer transition-colors"
                                        data-testid={`campaign-row-${r.id}`}>
                                        <td className="px-3 py-2">
                                            <div className="font-medium text-foreground">{r.title}</div>
                                            {r.description ? <div className="text-xs text-muted-foreground truncate max-w-[300px]">{r.description}</div> : null}
                                        </td>
                                        <td className="px-3 py-2 text-muted-foreground">{t(`campaignBuilder.type.${r.type}`) || r.type}</td>
                                        <td className="px-3 py-2 text-right text-foreground">{m.recipients_total ?? 0}</td>
                                        <td className="px-3 py-2 text-right text-foreground">{m.delivered ?? 0}</td>
                                        <td className="px-3 py-2 text-right text-emerald-600 dark:text-emerald-400">{m.seen ?? 0}</td>
                                        <td className="px-3 py-2 text-right text-amber-600 dark:text-amber-400">{m.acknowledged ?? 0}</td>
                                        <td className="px-3 py-2 text-right text-rose-600 dark:text-rose-400">{m.unopened ?? 0}</td>
                                        <td className="px-3 py-2">
                                            <Badge variant="outline" className={STATUS_TONE[r.status] || ''} data-testid={`campaign-status-${r.id}`}>
                                                {t(`campaignsTable.status.${r.status}`)}
                                            </Badge>
                                        </td>
                                        <td className="px-3 py-2 text-xs text-muted-foreground">
                                            <div>{r.created_by_name || '—'}</div>
                                            <div>{fmtLocalShort(r.created_at, i18n.language) || '—'}</div>
                                        </td>
                                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                                            {canCancel ? (
                                                <Button variant="ghost" size="sm" onClick={() => handleCancel(r.id)}
                                                    disabled={cancelingId === r.id}
                                                    data-testid={`campaign-cancel-${r.id}`}>
                                                    {cancelingId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                                                </Button>
                                            ) : null}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    );
};

export default CampaignsTable;
